import { Stack, Tooltip, Typography, TypographyProps } from '@mui/material';
import { formatDistance, formatRelative, isSameDay, isValid } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { isNil } from 'lodash';
import { ReactNode, useMemo } from 'react';

import { UserDisplayPreferences, usePreferredTimeZone } from '@/api/userAccount';
import { useNow } from '@/hooks/useNow';
import { useTzContext } from '@/hooks/useTzContext';
import { DateRange } from '@/store';
import { isFiniteNumber } from '@/utils';

// dates in the future are either clock skew or corrupt. if it's up to 5 minutes in the future,
// assume clock skew and call it "now"
const FUTURE_IS_NOW_MS = 5 * 60 * 1000;

export type TimestampProps = {
    value?: string | number | Date | null;
    /**
     * relative - 'yesterday at 12:00 pm'
     *
     * distanceToNow - '6 hours ago'
     *
     * long - 'January 2nd, 2021'
     *
     * medium - 'Jan 2, 2025'
     *
     * short - '01/02/1453' (mm/dd/yyyy)
     */
    type?: 'relative' | 'distanceToNow' | 'long' | 'medium' | 'short';
    unit?: 'date' | 'time' | 'datetime';
    includeSeconds?: boolean;
    fallback?: string;
} & TypographyProps;

const DATE_FORMATS = { short: 'P', medium: 'PP', long: 'PPP' };
const TIME_FORMAT_LOCAL = 'h:mm aaa';
const TIME_FORMAT_Z = 'HH:mm';
const TIME_FORMAT_WITH_SECS_LOCAL = 'h:mm:ss aaa';
const TIME_FORMAT_WITH_SECS_Z = 'HH:mm:ss';
const TOOLTIP_FORMAT = 'yyyy-MM-dd HH:mm:ss XX';
const TOOLTIP_FORMAT_WITH_MS = 'yyyy-MM-dd HH:mm:ss.SSS XX';

export const Timestamp = ({
    value,
    type = 'long',
    unit = 'datetime',
    fallback = 'Unknown',
    includeSeconds = false,
    sx,
    ...props
}: TimestampProps) => {
    const timeZone = usePreferredTimeZone();
    const utc = timeZone === 'UTC';
    const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tz = utc ? 'UTC' : localTz;
    const now = useNow(60, { enabled: type === 'relative' || type === 'distanceToNow' });

    const date: Date | null = useMemo(() => {
        if (!value) return null;
        if (value instanceof Date && !isValid(value)) {
            console.error('Timestamp received invalid date', value);
            return null;
        }

        let correctedValue;
        if (isFiniteNumber(value) && value < 10000000000) {
            // looks like seconds, so let's make it ms. Apologies to events before 1973-3-3 9:46:40Z
            correctedValue = value * 1000;
        } else {
            correctedValue = value;
        }

        try {
            return new Date(correctedValue);
        } catch (e) {
            console.error('Unable to parse date', value, e);
            return null;
        }
    }, [value]);

    const formatted = useMemo(
        () =>
            date
                ? formatTimestamp(date, {
                      type,
                      now,
                      timeZone,
                      unit,
                      includeSeconds,
                  })
                : null,
        [date, type, now, timeZone, unit, includeSeconds]
    );

    const tooltip = useMemo(() => {
        if (!date) return null;

        // provide one unit higher resolution in tooltip
        const tooltipFormat = includeSeconds ? TOOLTIP_FORMAT_WITH_MS : TOOLTIP_FORMAT;

        return (
            <>
                {formatInTimeZone(date, tz, tooltipFormat)}
                <br />
                {formatInTimeZone(date, utc ? localTz : 'UTC', tooltipFormat)}
            </>
        );
    }, [date, includeSeconds, localTz, tz, utc]);

    if (!date) {
        return (
            <Typography component="span" sx={{ fontStyle: 'italic', ...inheritParentTypography(sx), ...sx }} {...props}>
                {fallback}
            </Typography>
        );
    }

    return (
        <Tooltip enterDelay={300} title={tooltip}>
            <Typography
                component="span"
                sx={{
                    fontWeight: undefined, // this is a dummy to help mui types figure out which kind of sx this is
                    ...inheritParentTypography(sx),
                    ...sx,
                }}
                {...props}
            >
                {formatted}
            </Typography>
        </Tooltip>
    );
};

/**
 * Include this in your Typography sx to inherit parent styles if you're nested within a containing Typography.
 */
const inheritParentTypography = (sx: TypographyProps['sx']) => ({
    '.MuiTypography-root &': { fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', ...sx },
});

export type FormatTimestampOptions = {
    now?: Date;
    timeZone: UserDisplayPreferences['timeZone'];
} & Pick<TimestampProps, 'type' | 'unit' | 'includeSeconds'>;

export function formatTimestamp(
    date: Date,
    { type = 'long', unit = 'datetime', now, includeSeconds, timeZone }: FormatTimestampOptions
): string | null {
    const utc = timeZone === 'UTC';
    const tz = utc ? 'UTC' : Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (type === 'relative') {
        return formatRelative(toZonedTime(date, tz), toZonedTime(now ?? new Date(), tz));
    }
    if (type === 'distanceToNow') {
        const zonedDate = toZonedTime(date, tz);
        const zonedNow = toZonedTime(now ?? new Date(), tz);
        const msIntoFuture = zonedDate.getTime() - zonedNow.getTime();
        if (msIntoFuture >= -5 && msIntoFuture < FUTURE_IS_NOW_MS) {
            // We always use this for past events, so future is unusual. If it's just a little in
            // the future, it's probably clock skew, so let's call it now. If it's way in the future,
            // it's probably corrupt data so I guess we might as well show it.
            return 'just now';
        }
        return formatDistance(zonedDate, zonedNow, {
            addSuffix: true,
            includeSeconds,
        });
    }

    const dateFormat = DATE_FORMATS[type];
    const timeFormat = includeSeconds
        ? utc
            ? TIME_FORMAT_WITH_SECS_Z
            : TIME_FORMAT_WITH_SECS_LOCAL
        : utc
          ? TIME_FORMAT_Z
          : TIME_FORMAT_LOCAL;
    const format = {
        date: dateFormat,
        time: timeFormat,
        datetime: dateFormat + ', ' + timeFormat,
    }[unit];

    return formatInTimeZone(date, tz, format);
}

export type TimestampRangeProps = {
    dateRange: DateRange;
} & Pick<TimestampProps, 'unit' | 'includeSeconds' | 'sx' | 'variant'>;

export const TimestampRange = ({ dateRange, unit = 'date', includeSeconds, variant, sx }: TimestampRangeProps) => {
    const tz = useTzContext();
    const isOneDay = isSameDay(dateRange.startDate, dateRange.endDate, tz);

    if (isNil(dateRange.startDate)) {
        return false;
    }

    const timestampProps: Pick<TimestampProps, 'variant' | 'type' | 'includeSeconds'> = {
        variant: 'inherit',
        type: 'short',
        includeSeconds,
    };

    let firstGroup: ReactNode;
    let secondGroup: ReactNode = null;
    if (unit === 'date' && isOneDay) {
        firstGroup = <Timestamp {...timestampProps} value={dateRange.startDate} unit="date" />;
    } else if (unit === 'datetime' && isOneDay) {
        firstGroup = (
            <>
                <Timestamp {...timestampProps} value={dateRange.startDate} unit="date" />,
            </>
        );
        secondGroup = (
            <>
                <Timestamp {...timestampProps} value={dateRange.startDate} unit="time" />
                {' - '}
                <Timestamp {...timestampProps} value={dateRange.endDate} unit="time" />
            </>
        );
    } else {
        firstGroup = (
            <>
                <Timestamp {...timestampProps} value={dateRange.startDate} unit={unit} />
                {' -'}
            </>
        );
        secondGroup = <Timestamp {...timestampProps} value={dateRange.endDate} unit={unit} />;
    }

    return (
        <Stack direction="row" sx={{ flexWrap: 'wrap', columnGap: '0.2em', ...sx }}>
            <Typography component="span" variant={variant} sx={{ whiteSpace: 'nowrap' }}>
                {firstGroup}
            </Typography>
            {secondGroup && (
                <Typography component="span" variant={variant} sx={{ whiteSpace: 'nowrap' }}>
                    {secondGroup}
                </Typography>
            )}
        </Stack>
    );
};
